import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../../components/Header";
import { AssetFinanceUrl } from "../../../../config";
import { executeDynamicSubmit } from "../../../../data/DynamicServiceNew/jsondynamicservice";
import Swal from "sweetalert2";
import { NumericFormat } from "react-number-format";

const base_url = AssetFinanceUrl.uri;

const Form = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const assetTypeId = parseInt(localStorage.getItem("AssetTypeId"));

  const initialValues = {
    requirement: props.data ? props.data.Requirement : "REQUIRED",
    requiredForLien: props.data ? !!props.data.RequiredForLien : false,
    requiredAboveAmount: props.data
      ? props.data.RequiredAboveAmount
      : "",
    requiredIfThirdParty: props.data
      ? !!props.data.RequiredIfThirdParty
      : false,
    // ← NEW: checkbox state derived from OwnershipType. Checked only if the
    // saved value is exactly "COMPANY_OWNED"; NULL/anything else = unchecked.
    isCompanyOwnedOnly: props.data ? props.data.OwnershipType === "COMPANY_OWNED" : false,
    documentName: props.data ? props.data.DocumentName : "",
    displayOrder: props.data ? props.data.DisplayOrder : "",
    policyVersion: props.data ? props.data.PolicyVersion : 1,
    isActive: props.data ? !!props.data.IsActive : true,
    effectiveFrom: props.data
      ? props.data.EffectiveFrom?.split("T")[0]
      : "",
    effectiveTo: props.data?.EffectiveTo
      ? props.data.EffectiveTo.split("T")[0]
      : "",
  };

  const validationSchema = yup.object().shape({
    requirement: yup.string().required("Requirement is required"),
    documentName: yup.string().required("Document Name is required"),
    displayOrder: yup
      .number()
      .typeError("Enter a valid display order")
      .required("Display order is required"),
    requiredAboveAmount: yup.number().nullable(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const assetDocumentRequirement = {
      assetType: assetTypeId,
      requirement: values.requirement,
      requiredForLien: values.requiredForLien,
      requiredAboveAmount: values.requiredAboveAmount
        ? parseFloat(values.requiredAboveAmount)
        : null,
      requiredIfThirdParty: values.requiredIfThirdParty,
      documentName: values.documentName,
      displayOrder: parseInt(values.displayOrder),
      policyVersion: parseInt(values.policyVersion) || null,
      effectiveFrom: values.effectiveFrom || null,
      effectiveTo: values.effectiveTo || null,
      companyId: parseInt(decodedToken.CompanyId),
      isActive: values.isActive,
      // ← NEW: checkbox → ownershipType. Unchecked = null = applies to every
      // ownership type. Checked = "COMPANY_OWNED" = only shows for company-owned assets.
      ownershipType: values.isCompanyOwnedOnly ? "COMPANY_OWNED" : null,
    };

    if (props.isEditing) {
      assetDocumentRequirement.id = props.data.Id;
    }

    await executeDynamicSubmit({
      mutationName: props.isEditing
        ? "updateAssetDocumentRequirement"
        : "createAssetDocumentRequirement",
      variables: assetDocumentRequirement,
      mutationObjectName: "assetDocumentRequirement",
      baseUrl: base_url,
      isEditing: props.isEditing,
      entityName: "Asset Document Requirement",
      onSuccess: () => {
        props.onClose();
        props.onAction();
      },
      setSubmitting,
    });
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Asset Type" : "Create Asset Type"}
        subtitle={
          props.isEditing
            ? "Edit an Existing Collateral Asset Type"
            : "Create a New Collateral Asset Type"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                fullWidth
                variant="filled"
                label="Document Name *"
                name="documentName"
                value={values.documentName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.documentName && !!errors.documentName}
                helperText={touched.documentName && errors.documentName}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                select
                fullWidth
                variant="filled"
                label="Requirement"
                name="requirement"
                value={values.requirement}
                onChange={handleChange}
                sx={{ gridColumn: "span 2" }}
              >
                <MenuItem value="REQUIRED">Mandatory</MenuItem>
                <MenuItem value="CONDITIONAL">If Applicable</MenuItem>
                <MenuItem value="SUPPORTING">Optional</MenuItem>
              </TextField>

              <NumericFormat
                customInput={TextField}
                fullWidth
                variant="filled"
                label="Required Above Amount"
                name="requiredAboveAmount"
                value={values.requiredAboveAmount}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                onValueChange={(values) => {
                  setFieldValue("requiredAboveAmount", values.value);
                }}
                onBlur={handleBlur}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Display Order"
                name="displayOrder"
                value={values.displayOrder}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.displayOrder && !!errors.displayOrder}
                helperText={touched.displayOrder && errors.displayOrder}
                sx={{ gridColumn: "span 2" }}
              />

              <Box
                sx={{
                  gridColumn: "span 4",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 3,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.requiredForLien}
                      onChange={(e) =>
                        setFieldValue("requiredForLien", e.target.checked)
                      }
                    />
                  }
                  label="Required For Lien"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.requiredIfThirdParty}
                      onChange={(e) =>
                        setFieldValue(
                          "requiredIfThirdParty",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="Required If Third Party"
                />

                {/* ── NEW: Company-Owned-only toggle ── */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isCompanyOwnedOnly}
                      onChange={(e) =>
                        setFieldValue("isCompanyOwnedOnly", e.target.checked)
                      }
                    />
                  }
                  label="Company-Owned Asset Only (e.g. CR12, director letters)"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isActive}
                      onChange={(e) =>
                        setFieldValue("isActive", e.target.checked)
                      }
                    />
                  }
                  label="Active"
                />
              </Box>

              {values.isCompanyOwnedOnly && (
                <Box sx={{ gridColumn: "span 4" }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "8px",
                      bgcolor: "rgba(255, 193, 7, 0.1)",
                      border: "1px solid rgba(255, 193, 7, 0.4)",
                      fontSize: "0.8rem",
                    }}
                  >
                    This document will only appear when a collateral asset's
                    ownership is set to <b>Company Owned</b>. It will not show
                    for Single Owner or Co-Owned assets.
                  </Box>
                </Box>
              )}
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default Form;